import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";
import type { ITootMapPanelProps } from "./TootMapPanel.props";

const getSafeRel = (target?: string, rel?: string) => {
  if (target !== "_blank") {
    return rel;
  }

  const values = new Set((rel ?? "").split(/\s+/).filter((value) => value && value !== "opener"));
  values.add("noopener");
  values.add("noreferrer");

  return Array.from(values).join(" ");
};

export const TootMapPanel = ({
  addressSummary,
  className,
  directionsAriaLabel,
  directionsHref,
  directionsLabel,
  directionsRel,
  directionsTarget,
  directionsTitle,
  embed,
  providerLabel,
  title,
}: ITootMapPanelProps) => {
  const Link = resolveTootPrimitive("Link");

  if (!embed) {
    return null;
  }

  return (
    <section
      aria-label={title}
      className={cn(
        "group/map relative isolate h-full min-h-[32rem] overflow-hidden rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-orange)_38%,var(--color-toot-grid-strong))] bg-[radial-gradient(circle_at_76%_20%,rgba(255,77,46,0.2),transparent_18rem),linear-gradient(180deg,var(--color-toot-surface-terminal),var(--color-toot-black)_82%)] text-[var(--color-toot-foreground)] shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_1.5rem_4rem_rgba(0,0,0,0.36)]",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,77,46,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(255,138,31,0.08)_1px,transparent_1px)] bg-[size:1.65rem_1.65rem] opacity-55 [mask-image:linear-gradient(180deg,black,transparent_92%)]"
      />

      <div className="absolute inset-0 opacity-75 transition-opacity duration-300 ease-out group-hover/map:opacity-95 motion-reduce:transition-none [&_iframe]:h-full [&_iframe]:min-h-full [&_iframe]:w-full [&_iframe]:border-0 [&_iframe]:[filter:grayscale(1)_invert(0.92)_contrast(1.16)_brightness(0.72)]">
        {embed}
      </div>

      <div className="relative z-10 flex items-center justify-between gap-3 p-4 text-[0.64rem] font-black uppercase tracking-[0.18em] sm:p-5">
        {providerLabel ? (
          <span className="hidden min-w-0 truncate rounded-full border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_70%,transparent)] bg-toot-surface-raised/85 px-3 py-2 text-[color-mix(in_oklab,var(--color-toot-foreground)_72%,transparent)] sm:block">
            {providerLabel}
          </span>
        ) : null}
      </div>

      <div className="absolute inset-x-4 bottom-4 z-10 sm:inset-x-5 sm:bottom-5">
        <div className="grid gap-4 rounded-[var(--radius-toot-frame)] border border-[color-mix(in_oklab,var(--color-toot-grid-strong)_82%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-black)_84%,transparent)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div className="min-w-0 space-y-2">
            {title ? (
              <h3 className="m-0 text-pretty text-xl font-black leading-tight tracking-[-0.045em] text-[var(--color-toot-foreground)] md:text-2xl">
                {title}
              </h3>
            ) : null}
            {addressSummary ? (
              <p className="m-0 max-w-[46rem] text-sm leading-6 text-[color-mix(in_oklab,var(--color-toot-foreground)_78%,transparent)] [overflow-wrap:anywhere]">
                {addressSummary}
              </p>
            ) : null}
          </div>
          {directionsHref && directionsLabel ? (
            <Link
              aria-label={directionsAriaLabel}
              className="inline-flex min-h-11 items-center justify-center rounded-full border border-[color-mix(in_oklab,var(--color-toot-orange)_54%,transparent)] bg-[color-mix(in_oklab,var(--color-toot-red)_18%,var(--color-toot-black))] px-4 text-xs font-black uppercase tracking-[0.16em] text-[var(--color-toot-orange)] transition-colors duration-200 ease-out hover:bg-[color-mix(in_oklab,var(--color-toot-red)_28%,var(--color-toot-black))] motion-reduce:transition-none"
              href={directionsHref}
              rel={getSafeRel(directionsTarget, directionsRel)}
              target={directionsTarget}
              title={directionsTitle}
            >
              {directionsLabel}
            </Link>
          ) : null}
        </div>
      </div>
    </section>
  );
};

TootMapPanel.displayName = "Contact::TootMapPanel";
